import React, { useState } from "react";
import Flatpickr from "react-flatpickr";
import { withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
	Card,
	Badge,
	Table,
	ListGroup,
	ListGroupItem,
	Label,
	Input,
	Button,
	Row,
	Col,
} from "reactstrap";
import { HourlyActivityLog, Summary } from "store/audits/types";

type LogEntry = {
	timestamp: string | null;
	record_name: string | null;
	model: string;
	record: string;
	child_model: string | null;
	action: string;
	time_spent: string | null;
	details: string | null;
	points: number;
	gap: string | null;
};

type HourlyLog = {
	hour_range: string;
	activities: number;
	leads: number;
	points: number;
	logs: LogEntry[];
};

type OptionType = { value: string; label: string };

export interface Props {
	staffName: string;
	shift: string;
	remarks: string[];
	summary: Summary;
	hourlyLogs: HourlyActivityLog[];
	selectedDate?: Date;
	selectedAction?: string;
	selectedModel?: string;
	selectedChildModel?: string;
	uniqueOnly?: boolean;

	onActionChange?: (value: string) => void;
	onModelChange?: (value: string) => void;
	onChildModelChange?: (value: string) => void;
	onDateChange: (value: Date) => void
	onToggleUniqueOnly?: (val: boolean) => void;
	onRefresh?: () => void;

	actionOptions?: string[];
	modelOptions?: string[];
	childModelOptions?: string[];

	checkInTime?: string;
	checkOutTime?: string;

	t?: any;
}

const AuditLogCard: React.FC<Props> = ({
	staffName,
	shift,
	remarks,
	summary,
	selectedDate,
	hourlyLogs,
	selectedAction,
	selectedModel,
	selectedChildModel,
	uniqueOnly,
	onActionChange,
	onModelChange,
	onChildModelChange,
	onDateChange,
	onToggleUniqueOnly,
	onRefresh,
	actionOptions = [],
	modelOptions = [],
	childModelOptions = [],
	checkInTime,
	checkOutTime,
	t
}) => {
	const buildSelectOptions = (values: string[]) => [
		{ value: "", label: "All" },
		...values.map((v) => ({
			value: v,
			label: toLabel(v),
		})),
	];

	const toLabel = (key: string): string => {
		if (!key) return "";

		return key
			.replace(/([a-z])([A-Z])/g, "$1 $2")     // camelCase → camel Case
			.replace(/[_\-]+/g, " ")                 // snake_case, kebab-case → snake case
			.toLowerCase()                           // everything to lowercase
			.replace(/\b\w/g, (c) => c.toUpperCase()); // capitalize first letter of each word
	};

	const handleReset = () => {
		onActionChange?.("");
		onChildModelChange?.("");
		onModelChange?.("");
		onToggleUniqueOnly?.(false);
	};

	return (
		<>
			{/* Filters */}
			<div className="mb-1 bg-light px-3 py-3">
				<div className="d-flex justify-content-start align-items-center mb-2 gap-2">
					<h5 className="fw-semibold mb-0">{t("common.table.filters")}</h5>
					<Button
						color="danger"
						className="btn-label btn-sm"
						disabled={
							!(
								selectedAction ||
								selectedChildModel ||
								selectedModel ||
								selectedDate ||
								uniqueOnly
							)
						}
						onClick={handleReset}
					>
						<i className="ri-refresh-line label-icon align-middle fs-16 me-2"></i>
						{t("common.offcanvas.reset")}
					</Button>
				</div>

				<div className="row d-flex justify-content-between">
					{/* Action */}
					<div className="col-md-4 d-flex flex-column justify-content-between">
						<Label className="form-label text-muted fw-medium small mb-1 d-flex align-items-center">
							<i className="ri-flashlight-line me-1 text-primary" />
							{t("common.pagination.action")}
						</Label>
						<div className="text-muted small mb-1">
							{t("footprints.performed_activity_info_text")}
						</div>
						<Select
							options={buildSelectOptions(actionOptions)}
							value={{
								value: selectedAction || "",
								label: selectedAction ? toLabel(selectedAction) : "All",
							}}
							onChange={(opt: OptionType) => onActionChange?.(opt?.value || "")}
							isClearable
							classNamePrefix="react-select"
						/>
					</div>

					{/* Related Module */}
					<div className="col-md-4 d-flex flex-column justify-content-between">
						<Label className="form-label text-muted fw-medium small mb-1 d-flex align-items-center">
							<i className="ri-share-forward-line me-1 text-secondary" />
							{t("footprints.related_module")}
						</Label>
						<div className="text-muted small mb-1">
							{t("footprints.sub_entity_under_primary_model_info_text")}
						</div>
						<Select
							options={buildSelectOptions(childModelOptions || [])}
							value={{
								value: selectedChildModel || "",
								label: selectedChildModel ? toLabel(selectedChildModel) : "All",
							}}
							onChange={(opt: OptionType) => onChildModelChange?.(opt?.value || "")}
							isClearable
							classNamePrefix="react-select"
						/>
					</div>

					{/* Date Picker */}
					<div className="col-md-4 d-flex flex-column justify-content-between">
						<div>
							<Label className="form-label text-muted fw-medium small mb-1 d-flex align-items-center">
								<i className="ri-calendar-2-line me-1 text-danger" />
								{t("footprints.footprint_date")}
							</Label>
							<div className="text-muted small mb-1">
								{t("footprints.filter_logs_by_selected_date")}
							</div>
						</div>

						<div className="position-relative">

							<Flatpickr
								id="auditDate"
								className="form-control ps-5 pe-3 py-2"
								options={{
									dateFormat: "d M Y",
									maxDate: "today",
								}}
								value={selectedDate}
								onChange={(dates) => dates.length && onDateChange?.(dates[0])}
								placeholder={t("common.filter.search.select_date")}
							/>
							<i className="ri-calendar-2-line position-absolute top-50 start-0 translate-middle-y ps-3 text-primary" />
						</div>
					</div>
				</div>
			</div>
			<div className="d-flex flex-column gap-1">
				<div className="d-flex justify-content-end align-items-center">
					{onRefresh && (
						<Button
							color="link"
							className="p-0 align-self-end text-decoration-none"
							title="Refresh data"
							onClick={onRefresh}
						>
							<i className="ri-refresh-line fs-16 me-3 fw-bold"></i>
						</Button>
					)}
				</div>

				{hourlyLogs.length === 0 ? (
					<div className="text-center py-5 border rounded bg-light">
						<i className="ri-file-info-line fs-2 text-muted mb-2 d-block" />
						<h6 className="fw-semibold text-muted">{t("footprints.no_activity_logs_found")}</h6>
						<p className="text-muted small mb-0">
							{t("footprints.there_are_no_audit_logs_available_selected_filters_date")}
						</p>
					</div>
				) : (
					<>
						<div className="timeline-container px-2">
							{hourlyLogs.map((log, logIdx) => (
								<div key={logIdx} className="mb-1">
									{/* Hour Header */}
									<div className="d-flex justify-content-between align-items-center bg-light  p-1 rounded border">
										<div className="d-flex align-items-center">
											<i className="ri-time-line text-primary fs-5 me-2"></i>
											<h6 className="mb-0 text-primary fw-semibold">{log.hour_range}</h6>
										</div>
										<div className="text-muted me-3">
											<span className="badge rounded-pill bg-info-subtle text-info"><i className="ri-list-check-2 me-1"></i>{log.activities} Activities</span>
											{/* <i className="ri-list-check-2 me-1"></i>{log.activities} Activities */}
											<span className="mx-2">|</span>
											<span className="badge rounded-pill bg-success-subtle text-success"><i className="ri-user-follow-line me-1"></i>{log.leads} Leads</span>

											<span className="mx-2">|</span>
											<span className="badge rounded-pill bg-primary-subtle text-primary"><i className="ri-star-s-line me-1"></i>{log.points} Points</span>
										</div>
									</div>
									<div className="border-start border-2 border- ps-4 mt-2 position-relative">
										{log.logs.map((entry, idx) => (
											<div key={idx} className="position-relative px-2">
												{/* Timeline Dot */}
												<div
													className="position-absolute start-0 translate-middle bg-primary border border-white rounded-circle"
													style={{ width: "12px", height: "12px", top: "12px" }}
												></div>

												<div className="ms-3">
													{/* Timestamp + Lead Link */}
													<div className="d-flex justify-content-between align-items-center mb-2">
														<div>
															<span className="fw-semibold me-2">
																<i className="ri-calendar-check-line me-1 text-muted"></i>
																{entry.timestamp
																	? new Date(entry.timestamp.replace(/\[.*\]$/, "")).toLocaleTimeString([], {
																		hour: '2-digit',
																		minute: '2-digit',
																	})
																	: '–'}
															</span>

															{entry.record_name ? (
																<a
																	href={`/${entry.model}/${entry.record}`}
																	className="text-primary fw-semibold text-decoration-none"
																	title="View lead details"
																>
																	<i className="ri-external-link-line me-1"></i>
																	{entry.record_name}
																</a>
															) : (
																<span className="text-muted">–</span>
															)}
														</div>

														<span
															className={`me-3 badge rounded-pill border border-success text-success px-3 py-1 ${entry.points > 0
																// ? 'bg-success'
																// : entry.points === 0
																// 	? 'bg-warning text-dark'
																// 	: 'bg-secondary'
																}`}
														>
															{entry.points > 0 ? entry.points : '–'} &nbsp;pts
														</span>
													</div>

													{/* Metadata */}
													<div className="small text-muted mb-2">
														<i className="text-warning ri-shapes-fill me-1"></i>
														{entry.child_model ? toLabel(entry.child_model) : entry.model ? toLabel(entry.model) : '–'} |
														<i className="text-warning ri-play-circle-fill mx-1"></i>{toLabel(entry.action) || '–'} |
														<i className="text-warning ri-time-fill mx-1"></i>{entry.time_spent || '–'} |
														Gap: {entry.gap || '–'}
													</div>

													{/* Details */}
													<div
														className="border-start ps-3 text-wrap text-break text-muted small mb-2"
														style={{ borderColor: '#dee2e6', maxHeight: '120px', overflowY: 'auto', whiteSpace: 'pre-line' }}
														dangerouslySetInnerHTML={{ __html: entry.details || '<span class="text-muted">–</span>' }}
													/>
												</div>
												<hr></hr>
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default withTranslation()(AuditLogCard);
